import { US_TIMEZONES } from "@/lib/campaign";
import { localDay } from "@/lib/calendars";
import { isDay } from "@/lib/date-picker";
import { stateName } from "@/lib/firms";
import { sameValues } from "@/lib/form";
import type { ContactInput } from "@/lib/queries";
import { type FormFieldType, type RecordField, type RecordSection, type Role, atLeast } from "@/lib/types";
import { US_PHONE_PROBLEM, usPhone } from "@/lib/phone-number";
import { formatDateTime, formatPhoneNumber } from "@/lib/utils";

/**
 * A record's own fields as the console shows and edits them (ACL-255, ADR-087 in `platform`): the
 * sections an admin defined on the contact form, each field with its value and what the signed-in
 * person may do with it.
 *
 * The server decides everything here. It leaves out a field hidden from the reader's role, says
 * `read` or `edit` for the rest, and checks every value against its type (`services/shared/fields.py`).
 * The copies below keep a save it would refuse off the screen and say why first, as the contact
 * form does. Pure, for `test/record-fields.test.ts`.
 */

// --- what a section shows --------------------------------------------------------------------------------

/**
 * An admin's own fields in a section (`storage: "value"`), the ones the form's route saves. The
 * platform's fields (the name, the phone, the e-mail...) are saved where they always lived; a firm's
 * panel leaves them out, as the firm page's cards draw them, and a contact's draws them too
 * (`panelFields`).
 */
export function ownFields(section: RecordSection): RecordField[] {
  return section.fields.filter((f) => f.storage === "value" || f.storage === "accident");
}

/**
 * The fields a record's panel draws. A contact's panel is the record's one list of details, as
 * GoHighLevel's is, so it draws the platform's fields too (the name, the phone, the e-mail...) where the
 * form puts them, and they are changed there (`detailEditable`); a firm's draws its own fields only, the
 * firm page's cards drawing the rest.
 */
export function panelFields(section: RecordSection, withSystem = false): RecordField[] {
  return withSystem ? section.fields : ownFields(section);
}

/** How many of the fields a folder draws hold an answer, for its header. */
export function filledCount(section: RecordSection, withSystem = false): number {
  return panelFields(section, withSystem).filter((field) => fieldText(field) !== null).length;
}

/** The folders the details panel's search keeps (ACL-405), as GoHighLevel's "Search fields and folders":
 *  `searchSections` below is the Conversations panel's (ACL-392). A folder whose title
 *  matches keeps every field; otherwise only the fields whose title or answer matches, and a folder left
 *  with none is left out. An empty search keeps everything. `keep` names fields kept whatever the search
 *  says: one being changed, or changed since the search was typed, so it does not vanish when its new
 *  answer no longer matches. */
export function searchFolders(
  sections: readonly RecordSection[],
  query: string,
  withSystem = false,
  keep: ReadonlySet<string> = new Set(),
): RecordSection[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...sections];
  return narrowSections(
    sections,
    withSystem,
    keep,
    (field, section) =>
      section.title.toLowerCase().includes(needle) ||
      field.title.toLowerCase().includes(needle) ||
      (recordText(field) ?? "").toLowerCase().includes(needle),
  );
}

/** The sections with only the fields a search or a filter keeps (`keeps`, or named in `keep`), and a
 *  folder left with none left out. */
function narrowSections(
  sections: readonly RecordSection[],
  withSystem: boolean,
  keep: ReadonlySet<string>,
  keeps: (field: RecordField, section: RecordSection) => boolean,
): RecordSection[] {
  return sections.flatMap((section) => {
    const fields = panelFields(section, withSystem).filter((field) => keep.has(field.key) || keeps(field, section));
    return fields.length > 0 ? [{ ...section, fields }] : [];
  });
}

/** What the panel's filter keeps, as GoHighLevel's: every field, only those with an answer, or only the
 *  empty ones. */
export type FieldFilter = "all" | "filled" | "empty";

/**
 * The sections a filter keeps: only the fields with an answer (`filled`, "Hide empty fields and
 * folders") or only those without (`empty`, "Show only empty fields and folders"), and a folder left
 * with none is left out. `keep` names fields shown whatever they hold: the ones changed since the
 * filter was picked, so a field just filled in (or emptied) does not vanish under the person's hand.
 */
export function filterSections(
  sections: readonly RecordSection[],
  filter: FieldFilter,
  withSystem = false,
  keep: ReadonlySet<string> = new Set(),
): RecordSection[] {
  if (filter === "all") return [...sections];
  return narrowSections(sections, withSystem, keep, (field) => (recordText(field) === null) === (filter === "empty"));
}

/** What the panel says when the search and the filter leave nothing. */
export function nothingShownText(query: string, filter: FieldFilter): string {
  const q = query.trim();
  if (filter === "all") return `No field or folder matches “${q}”.`;
  if (q) return `No ${filter === "empty" ? "empty" : "answered"} field matches “${q}”.`;
  return filter === "empty" ? "No empty fields: every field has an answer." : "No field has an answer yet.";
}

/** The sections worth a folder: those with at least one field to draw. */
export function shownSections(sections: readonly RecordSection[], withSystem = false): RecordSection[] {
  return sections.filter((s) => panelFields(s, withSystem).length > 0);
}

/** The contact's own folder (the name, the phone, when the accident was and who was in it): what a rep
 *  reads as a call starts, so a contact's panel draws it first and keeps it open (ACL-526). Known by its
 *  key, which an admin cannot change; its title they can. */
export const CONTACT_SECTION = "contact";

/** The sections with the contact's own folder first and the rest in the form's order. The form keeps
 *  the order an admin gave it; only the panel draws Contact first. */
export function contactFirst(sections: readonly RecordSection[]): RecordSection[] {
  const i = sections.findIndex((s) => s.key === CONTACT_SECTION);
  return i <= 0 ? [...sections] : [sections[i]!, ...sections.slice(0, i), ...sections.slice(i + 1)];
}

/**
 * The sections a search finds, narrowed to what it found: a section whose title matches keeps all its
 * fields, the others keep the fields (of those `fieldsOf` draws) whose title or value (as `fieldText`
 * reads it) matches, and a section with no hit is left out. An empty search keeps every section with
 * a field to draw, as it is. The record's cards draw a section's own fields; the Conversations panel
 * (ACL-392) draws all of them, `section.fields`, as the Custom fields table lists them.
 */
export function searchSections(
  sections: readonly RecordSection[],
  search: string,
  fieldsOf: (section: RecordSection) => RecordField[] = ownFields,
): RecordSection[] {
  const q = search.trim().toLowerCase();
  const shown = sections.filter((s) => fieldsOf(s).length > 0);
  if (!q) return shown;
  const out: RecordSection[] = [];
  for (const section of shown) {
    if (section.title.toLowerCase().includes(q)) {
      out.push(section);
      continue;
    }
    const hits = fieldsOf(section).filter(
      (f) => f.title.toLowerCase().includes(q) || (fieldText(f) ?? "").toLowerCase().includes(q),
    );
    if (hits.length > 0) out.push({ ...section, fields: hits });
  }
  return out;
}

/** Whether this reader may change the field here: their role may, and its value is stored by the form
 *  (the platform's own fields are written where they always were). */
export function editable(field: RecordField): boolean {
  return field.can_edit && (field.storage === "value" || field.storage === "accident");
}

/** What a reference reads as: the linked record's name, or why it has none. A contact this reader may
 *  not open is never named, as the server sends it. */
export function referenceText(field: Pick<RecordField, "config" | "reference">): string | null {
  const ref = field.reference;
  if (!ref) return null;
  if (ref.state === "missing") return `A ${field.config.target === "lead" ? "contact" : "firm"} that no longer exists`;
  if (ref.state === "hidden") return "A contact you cannot open";
  const name = ref.label ?? String(ref.id);
  // An archived record keeps its link, and says so: it can no longer be picked for a new one (ACL-261).
  return ref.state === "archived" ? `${name} (archived)` : name;
}

/** Where a linked record opens, for one that exists and the reader may see, archived ones included. A
 *  firm's page is for a supervisor and above (`firms/:firmId` in `App.tsx`), so a rep is given the name
 *  to read and no link to a page that would refuse them. */
export function referenceLink(field: Pick<RecordField, "config" | "reference">, role: Role | undefined): string | null {
  const ref = field.reference;
  if (!ref || (ref.state !== "ok" && ref.state !== "archived")) return null;
  if (field.config.target !== "firm") return `/contacts/${ref.id}`;
  return atLeast(role, "supervisor") ? `/firms/${ref.id}` : null;
}

/** Whether a section's card offers Edit: only when there is something in it this person may change. */
export function canEditSection(section: RecordSection): boolean {
  return ownFields(section).some(editable);
}

// --- reading a value -------------------------------------------------------------------------------------

function optionLabel(field: RecordField, key: string): string {
  return field.config.options?.find((o) => o.key === key)?.label ?? key;
}

/** A date with no time and no zone: "2026-09-01" is the 1st wherever the reader is. */
function formatDate(text: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!m) return text;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" });
}

/**
 * The value as a person reads it, or null when nothing is stored. A dropdown shows its option's
 * label; an option an admin has since taken away shows its stored key rather than nothing, since
 * the record still holds it.
 */
export function fieldText(field: RecordField): string | null {
  const v = field.value;
  if (v === null || v === undefined || v === "") return null;
  switch (field.type) {
    case "boolean":
      return v === true ? "Yes" : v === false ? "No" : null;
    case "date":
      return formatDate(String(v));
    case "datetime":
      return formatDateTime(String(v));
    case "phone":
      return formatPhoneNumber(String(v));
    case "dropdown":
      return optionLabel(field, String(v));
    case "multi_select":
      return Array.isArray(v) && v.length > 0 ? v.map((k) => optionLabel(field, String(k))).join(", ") : null;
    case "reference":
      return referenceText(field);
    default:
      return String(v);
  }
}

/** A contact's timezone reads as its name ("Pacific"); one outside that list reads as stored. Their state
 *  reads as the State list offers it, "California (CA)". Every other field reads as `fieldText`. */
export function recordText(field: RecordField): string | null {
  if (field.system && field.key === "timezone" && typeof field.value === "string" && field.value) {
    return US_TIMEZONES.find((tz) => tz.zone === field.value)?.label ?? field.value;
  }
  if (field.system && field.key === "state" && typeof field.value === "string" && field.value) {
    return stateName(field.value);
  }
  return fieldText(field);
}

/** What an empty field says in words, or null when it reads as a dash. A contact with no timezone is
 *  called by its state's, so it says so; every other empty field is a dash, as on GoHighLevel's
 *  contact panel, so a folder of questions not asked yet is not a column of the same words (ACL-519). */
export function emptyText(field: RecordField): string | null {
  return field.system && field.key === "timezone" ? "From the state" : null;
}

/** GoHighLevel marks a question it needs with a "*" at the end of its name. The contact form keeps
 *  GHL's names word for word (ADR-118), and none of those fields is flagged `required`. */
const GHL_NEEDED = /\s*\*\s*$/;

/** Whether the record marks a field as needing an answer: flagged required, or named with GHL's "*"
 *  (ACL-519). The mark and nothing more: what may be saved, and what an intake call may skip, still go
 *  by `required` alone. */
export function markedNeeded(field: Pick<RecordField, "required" | "title">): boolean {
  return field.required || GHL_NEEDED.test(field.title);
}

/** A title as the record draws it beside its own red mark: GHL's "*" taken off, so it is not there twice. */
export function markedTitle(title: string): string {
  return title.replace(GHL_NEEDED, "") || title;
}

// --- laying a folder out --------------------------------------------------------------------------------

/** The types whose answer is a word or two: two such fields can share a row. */
const SHORT_TYPES: ReadonlySet<FormFieldType> = new Set(["text", "phone", "number", "date", "dropdown", "boolean"]);
/** The types whose answer reads as a tag on the summary card. */
const KEY_ANSWER_TYPES: ReadonlySet<FormFieldType> = new Set(["dropdown", "boolean", "text"]);
/** A title this long or shorter fits half the panel's width on one line. */
export const SHORT_TITLE = 18;

/** Whether a field is short enough to share a row: a short answer under a short title that is not a
 *  question (a question reads better across the whole width). */
export function isShortField(field: Pick<RecordField, "type" | "title">): boolean {
  return SHORT_TYPES.has(field.type) && field.title.length <= SHORT_TITLE && !field.title.includes("?");
}

/** The rows a folder lays its fields out in, in the form's order: two short fields one after the other
 *  share a row (first and last name, phone and e-mail), every other field has a row of its own. */
export function fieldRows(fields: readonly RecordField[]): RecordField[][] {
  const rows: RecordField[][] = [];
  for (let i = 0; i < fields.length; i += 1) {
    const field = fields[i]!;
    const next = fields[i + 1];
    if (next && isShortField(field) && isShortField(next)) {
      rows.push([field, next]);
      i += 1;
    } else {
      rows.push([field]);
    }
  }
  return rows;
}

/** At most this many answers on the record's summary card. */
export const KEY_ANSWERS_MAX = 6;
/** An answer longer than this is left off the summary card: a tag holds a few words. */
const KEY_ANSWER_CHARS = 40;

/**
 * The answers the record's summary card shows as tags: the first folder's own questions that hold a
 * short answer (on GoHighLevel's contact form the first folder holds the qualifying questions: when,
 * who was involved, medical help, fault, a lawyer), in the form's order, at most `KEY_ANSWERS_MAX`.
 */
export function keyAnswers(sections: readonly RecordSection[]): { key: string; title: string; text: string }[] {
  const first = sections.find((s) => ownFields(s).length > 0);
  if (!first) return [];
  const out: { key: string; title: string; text: string }[] = [];
  for (const field of ownFields(first)) {
    if (!KEY_ANSWER_TYPES.has(field.type)) continue;
    const text = fieldText(field);
    if (text === null || text.length > KEY_ANSWER_CHARS) continue;
    out.push({ key: field.key, title: field.title, text });
    if (out.length === KEY_ANSWERS_MAX) break;
  }
  return out;
}

// --- typing a value --------------------------------------------------------------------------------------

/** What an input holds while a person edits: text for most types, "yes"/"no"/"" for a Yes / No (so
 *  it can be left unanswered), the picked keys for a multi-select. */
export type Draft = string | string[];

const pad = (n: number) => String(n).padStart(2, "0");

/** A stored moment (ISO, UTC) as a `datetime-local` input holds it: the reader's own clock. */
function localInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** The draft a field starts from. */
export function draftOf(field: RecordField): Draft {
  const v = field.value;
  if (field.type === "multi_select") return Array.isArray(v) ? v.map(String) : [];
  if (v === null || v === undefined) return "";
  switch (field.type) {
    case "boolean":
      return v === true ? "yes" : v === false ? "no" : "";
    case "datetime":
      return localInput(String(v));
    case "phone":
      return formatPhoneNumber(String(v));
    default:
      return String(v);
  }
}

/** Every field's starting draft, by key. */
export function draftsOf(fields: readonly RecordField[]): Record<string, Draft> {
  return Object.fromEntries(fields.map((f) => [f.key, draftOf(f)]));
}

/** `TEXT_MAX`, `LONG_TEXT_MAX`, `EMAIL_MAX`, `NUMBER_DIGITS`, `NUMBER_PLACES` in `fields.py`. */
export const TEXT_MAX = 1000;
export const LONG_TEXT_MAX = 10_000;
export const EMAIL_MAX = 254;
export const NUMBER_DIGITS = 15;
export const NUMBER_PLACES = 6;

/** The longest a text field takes: its own limit, else its type's. */
export function maxLength(field: RecordField): number | undefined {
  if (field.type === "text") return field.config.max_length ?? TEXT_MAX;
  if (field.type === "long_text") return field.config.max_length ?? LONG_TEXT_MAX;
  if (field.type === "email") return EMAIL_MAX;
  return undefined;
}

const NUMBER_RE = /^[+-]?(\d+)(?:\.(\d+))?$/;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Dates that have already happened by their nature, the accident and the birthday (ACL-577): core-api's
 *  `forms.PAST_ONLY`, which refuses a later day however it is sent. */
export const PAST_ONLY_DATES: ReadonlySet<string> = new Set(["date_of_incident", "date_of_birth"]);
/** Whose today that is, as core-api's `forms.TODAY_ZONE`: Puerto Rico's, the first clock a lead is called on
 *  to turn (with New York's in summer, an hour before it in winter). */
const TODAY_ZONE = "America/Puerto_Rico";

/** The last day a date field takes (today, on core-api's clock), or nothing when any day goes. */
export function latestDay(field: { key: string; type: string | null }, now = new Date()): string | undefined {
  if (field.type !== "date" || !PAST_ONLY_DATES.has(field.key)) return undefined;
  return localDay(now.toISOString(), TODAY_ZONE);
}

export type Parsed = { ok: true; value: unknown } | { ok: false; problem: string };

const ok = (value: unknown): Parsed => ({ ok: true, value });
const bad = (problem: string): Parsed => ({ ok: false, problem });

/**
 * What to send for a draft, or what is wrong with it. An empty draft is null, which clears the
 * value. A number is sent as the text typed, which the server reads exactly (a JSON number would go
 * through a double on the way). The checks are `fields.py`'s, in its words where a person reads them.
 */
export function parseDraft(field: RecordField, draft: Draft): Parsed {
  if (Array.isArray(draft)) {
    if (draft.length === 0) return ok(null);
    const keys = (field.config.options ?? []).map((o) => o.key);
    if (draft.some((k) => !keys.includes(k))) return bad("Pick from the options");
    // In the options' order, as the server stores them.
    return ok(keys.filter((k) => draft.includes(k)));
  }
  const text = draft.trim();
  if (!text) return ok(null);
  switch (field.type) {
    case "text":
    case "long_text": {
      const limit = maxLength(field) ?? TEXT_MAX;
      if (field.type === "text" && /[\r\n]/.test(text)) return bad("Keep it on one line");
      if (text.length > limit) return bad(`At most ${limit.toLocaleString("en-US")} characters`);
      return ok(text);
    }
    case "email":
      if (text.length > EMAIL_MAX || !EMAIL_RE.test(text)) return bad("That does not look like an email address");
      return ok(text.toLowerCase());
    case "phone": {
      // A US number, stored with its +1 (ACL-653). One already stored in another spelling still reads.
      const us = usPhone(text);
      if (us) return ok(us);
      return text === draftOf(field) ? ok(text) : bad(US_PHONE_PROBLEM);
    }
    case "number": {
      const m = NUMBER_RE.exec(text.replace(/,/g, ""));
      if (!m) return bad("Enter a number, like 12 or 12.5");
      // As Python's Decimal counts them: leading zeros are not digits, trailing ones are, and a
      // trailing zero after the point is a decimal place ("1.50" has two).
      const places = m[2] ?? "";
      if ((m[1] + places).replace(/^0+(?=\d)/, "").length > NUMBER_DIGITS) {
        return bad(`At most ${NUMBER_DIGITS} digits`);
      }
      const allowed = field.config.decimals ?? NUMBER_PLACES;
      if (places.length > allowed) {
        return bad(allowed === 0 ? "A whole number" : `At most ${allowed} decimal place${allowed === 1 ? "" : "s"}`);
      }
      const n = Number(text.replace(/,/g, ""));
      if (field.config.min !== undefined && n < field.config.min) return bad(`At least ${field.config.min}`);
      if (field.config.max !== undefined && n > field.config.max) return bad(`At most ${field.config.max}`);
      return ok(text.replace(/,/g, ""));
    }
    case "date": {
      // A day that exists: `Date.parse` takes Feb 30 as Mar 2.
      if (!isDay(text)) return bad("Pick a date");
      const latest = latestDay(field);
      if (latest && text > latest) return bad("Cannot be later than today");
      return ok(text);
    }
    case "datetime": {
      // A `datetime-local` value is the reader's own clock; sent in UTC, as the server stores it.
      const d = new Date(text);
      if (Number.isNaN(d.getTime())) return bad("Pick a date and a time");
      return ok(d.toISOString());
    }
    case "boolean":
      return text === "yes" ? ok(true) : text === "no" ? ok(false) : bad("Yes or No");
    case "dropdown":
      return (field.config.options ?? []).some((o) => o.key === text) ? ok(text) : bad("Pick one of the options");
    case "reference":
      // The id of the record picked: a firm's is a number, a contact's a uuid. The server checks it
      // exists and that the reader may open it.
      if (field.config.target === "firm") return /^\d+$/.test(text) ? ok(Number(text)) : bad("Pick a firm");
      return UUID_RE.test(text) ? ok(text.toLowerCase()) : bad("Pick a contact");
    default:
      return ok(text);
  }
}

/**
 * The change to send for a section: only the fields this person may edit whose draft moved from
 * where it started, so saving one answer never re-sends (and re-checks) the others. `problems` names
 * each draft that cannot be sent, by key; when there are any, nothing is sent. Emptying a required
 * field that holds an answer is one of them, as the server refuses it (ACL-256).
 */
export function fieldChanges(
  fields: readonly RecordField[],
  drafts: Readonly<Record<string, Draft>>,
): { values: Record<string, unknown>; problems: Record<string, string> } {
  const values: Record<string, unknown> = {};
  const problems: Record<string, string> = {};
  for (const field of fields) {
    if (!editable(field)) continue;
    const draft = drafts[field.key];
    if (draft === undefined || sameValues(draft, draftOf(field))) continue;
    const parsed = parseDraft(field, draft);
    if (!parsed.ok) problems[field.key] = parsed.problem;
    // As the server: a required field may start empty, but an answer in it cannot be emptied.
    else if (parsed.value === null && field.required) problems[field.key] = "Required: it cannot be left empty";
    else values[field.key] = parsed.value;
  }
  return { values, problems };
}

/**
 * A refusal split the way the drawer shows it: "key: why" for a field in the section goes under that
 * field, as the console's own checks do, and anything else (a 403, a 409, a key the drawer does not
 * show) stays one message, named by titles. A 422 lists every bad value at once, "; "-separated.
 */
export function refusalProblems(
  message: string,
  fields: readonly RecordField[],
): { problems: Record<string, string>; rest: string | null } {
  const keys = new Set(fields.map((f) => f.key));
  const problems: Record<string, string> = {};
  const rest: string[] = [];
  for (const part of message.split("; ")) {
    const [, key = "", why = ""] = /^([a-z][a-z0-9_]*): (.+)$/.exec(part) ?? [];
    if (keys.has(key) && why) problems[key] = `${why.charAt(0).toUpperCase()}${why.slice(1)}`;
    else rest.push(part);
  }
  return { problems, rest: rest.length ? refusalText(rest.join("; "), fields) : null };
}

/**
 * The server names fields by their stored key ("accident_date: must be a date (YYYY-MM-DD)", "You
 * cannot change injury, notes"); a person knows them by their title. Only a key in the place the
 * server puts one is replaced, so a word in the message that happens to be a key is left alone.
 */
export function refusalText(message: string, fields: readonly RecordField[]): string {
  const titles = new Map(fields.map((f) => [f.key, f.title]));
  const named = (key: string) => titles.get(key) ?? key;
  const cannot = /^You cannot change (.+)$/.exec(message);
  if (cannot?.[1]) return `You cannot change ${cannot[1].split(", ").map(named).join(", ")}`;
  return message
    .split("; ")
    .map((part) => {
      const m = /^([a-z][a-z0-9_]*)(: | )(.*)$/.exec(part);
      const [, key = "", gap = "", why = ""] = m ?? [];
      return titles.has(key) ? `${named(key)}${gap}${why}` : part;
    })
    .join("; ");
}

// --- the contact's own details, changed on the record ---------------------------------------------------

/** `ContactUpdate`'s limits. */
export const DETAIL_LIMITS = { name: 120, phoneMin: 3, phone: 40 } as const;
const STATE_RE = /^[A-Za-z]{2}$/;

const nameText = (text: string): Parsed =>
  text.length > DETAIL_LIMITS.name ? bad(`At most ${DETAIL_LIMITS.name} characters`) : ok(text || null);
const optionalText = (text: string): Parsed => ok(text || null);
const asText = (value: unknown): string | null => (typeof value === "string" ? value : null);

/** One of the contact's own details as the record changes it: the least role that may (core-api's
 *  `CONTACT_REP_FIELDS` for a rep, a supervisor for the rest), how a typed value is checked (empty
 *  clears, except the phone, which every contact keeps), and the `PATCH /leads/{id}` body for a checked
 *  value, typed key by key so the compiler holds each to `ContactInput`. */
interface DetailRule {
  minRole: Role;
  parse: (text: string) => Parsed;
  input: (value: unknown) => ContactInput | null;
}

/**
 * The contact's own details the record changes, one entry each. Mirrors core-api's `ContactUpdate` and
 * `CONTACT_REP_FIELDS` (`PATCH /leads/{id}`): a rep changes the names, the language, the residence state
 * (the rep on the call is who hears where the lead is; a new state is the lead's clock, ACL-576) and the
 * e-mail (Send contract needs it, ACL-541), a supervisor also the phone. The state and the e-mail leave a
 * system note. The timezone is read only: the state is how the console says where the lead is, and the
 * server clears a stored zone when the state changes (platform ADR-197). The source and the consent are
 * nobody's to change here. The server checks it all again. A Map, so a key the server sends never reads a
 * prototype's property.
 */
const DETAILS: ReadonlyMap<string, DetailRule> = new Map<string, DetailRule>([
  ["first_name", { minRole: "rep", parse: nameText, input: (v) => ({ first_name: asText(v) }) }],
  ["last_name", { minRole: "rep", parse: nameText, input: (v) => ({ last_name: asText(v) }) }],
  ["language", { minRole: "rep", parse: optionalText, input: (v) => ({ language: asText(v) }) }],
  [
    "phone",
    {
      minRole: "supervisor",
      parse: (text) => {
        if (text.length < DETAIL_LIMITS.phoneMin) return bad("A number is required");
        const us = usPhone(text);
        return us ? ok(us) : bad(US_PHONE_PROBLEM);
      },
      input: (v) => (typeof v === "string" ? { phone: v } : null),
    },
  ],
  [
    "email",
    {
      minRole: "rep",
      parse: (text) => {
        if (!text) return ok(null);
        return text.length > EMAIL_MAX || !EMAIL_RE.test(text) ? bad("That does not look like an email address") : ok(text);
      },
      input: (v) => ({ email: asText(v) }),
    },
  ],
  [
    "state",
    {
      minRole: "rep",
      parse: (text) => {
        if (!text) return ok(null);
        return STATE_RE.test(text) ? ok(text.toUpperCase()) : bad("Two letters, like CA");
      },
      input: (v) => ({ state: asText(v) }),
    },
  ],
]);

/** Whether this person may change one of the contact's own details (a system field) on the record. */
export function detailEditable(field: Pick<RecordField, "key" | "system">, role: Role | undefined): boolean {
  const rule = field.system ? DETAILS.get(field.key) : undefined;
  return rule !== undefined && atLeast(role, rule.minRole);
}

/** What to send for a detail typed on the record, or what is wrong with it. */
export function parseDetail(field: Pick<RecordField, "key">, draft: Draft): Parsed {
  const text = (Array.isArray(draft) ? "" : draft).trim();
  return DETAILS.get(field.key)?.parse(text) ?? bad("This is not changed on the record");
}

/** The `PATCH /leads/{id}` body for one checked detail (`parseDetail`'s value), or null for a key the
 *  record does not change. */
export function detailInput(key: string, value: unknown): ContactInput | null {
  return DETAILS.get(key)?.input(value) ?? null;
}

/**
 * Whether a field saves only on Enter or Save, and is put back when it is left: the phone and the
 * e-mail. The phone is what the dialer calls and the consent and do-not-call hang on it, so a slip of
 * the mouse while reading the record must not save a new one. Every other answer saves on leaving it.
 */
export function confirmsSave(field: Pick<RecordField, "key" | "system">): boolean {
  return field.system && (field.key === "phone" || field.key === "email");
}

/** What to send for one of the form's own fields typed on the record, or what is wrong with it: one
 *  field's `fieldChanges`. */
export function parseOwn(field: RecordField, draft: Draft): Parsed {
  const parsed = parseDraft(field, draft);
  if (parsed.ok && parsed.value === null && field.required) return bad("Required: it cannot be left empty");
  return parsed;
}
